import { ReactNode, useMemo, useRef } from 'react';
import * as R from 'remeda';
import { IconX } from '@rossum/ui/icons/tabler';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  SxProps,
  Theme,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { OverflowContainer } from '../overflow-container/OverflowContainer';
import {
  createSelectionPanelItem,
  SelectionPanelActionDescriptor,
} from './createSelectionPanelItem';
import { useSelectionPanelOverflowBudget } from './useSelectionPanelOverflowBudget';

type SelectionPanelProps = {
  visible: boolean;
  selectedLabel: ReactNode;
  onDeselect: () => void;
  actions: ReadonlyArray<SelectionPanelActionDescriptor>;
  sx?: SxProps<Theme>;
  'data-cy'?: string;
};

const SelectionPanel = ({
  visible,
  selectedLabel,
  onDeselect,
  actions,
  sx,
  'data-cy': dataCy,
}: SelectionPanelProps) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const labelRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const budget = useSelectionPanelOverflowBudget({
    visible,
    rootRef,
    labelRef,
    closeButtonRef,
  });

  const items = useMemo(
    () => R.map(actions, createSelectionPanelItem),
    [actions]
  );

  if (!visible) {
    return null;
  }

  return (
    <Box ref={rootRef} sx={{ position: 'relative', width: '100%', height: 0 }}>
      <Paper
        elevation={4}
        component={Stack}
        alignItems="center"
        justifyContent="space-between"
        direction="row"
        sx={{
          position: 'absolute',
          width: 'fit-content',
          maxWidth: '100%',
          px: 2,
          minHeight: theme => theme.spacing(6.75),
          backgroundColor: theme => theme.palette.background.paper,
          borderRadius: borderRadius('m'),
          zIndex: 10,
          ...sx,
        }}
        data-cy={dataCy}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ overflow: 'hidden', minWidth: 0, flex: '0 1 auto' }}
        >
          <Box ref={labelRef} sx={{ flexShrink: 0 }}>
            {selectedLabel}
          </Box>
          <OverflowContainer items={items} budget={budget} />
        </Stack>
        <IconButton
          ref={closeButtonRef}
          onClick={onDeselect}
          data-cy="selection-panel-deselect"
          sx={{ flexShrink: 0 }}
        >
          <SvgIcon fontSize="medium">
            <IconX />
          </SvgIcon>
        </IconButton>
      </Paper>
    </Box>
  );
};

export { SelectionPanel };
export type { SelectionPanelProps };
